import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-f885d650"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "calendar-events-container" }
const _hoisted_2 = { class: "calendar-header" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Calendar Events", -1))
const _hoisted_4 = { class: "calendar-actions" }
const _hoisted_5 = {
  key: 0,
  class: "loading"
}
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "Loading events...", -1))
const _hoisted_7 = [
  _hoisted_6
]
const _hoisted_8 = {
  key: 1,
  class: "error-message"
}
const _hoisted_9 = {
  key: 2,
  class: "calendar-grid"
}
const _hoisted_10 = { class: "calendar-nav" }
const _hoisted_11 = { class: "calendar-weekdays" }
const _hoisted_12 = { class: "calendar-days" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { class: "day-number" }
const _hoisted_15 = {
  key: 0,
  class: "day-events"
}
const _hoisted_16 = {
  key: 0,
  class: "more-events"
}
const _hoisted_17 = {
  key: 3,
  class: "modal-overlay"
}
const _hoisted_18 = { class: "modal-content" }
const _hoisted_19 = { class: "modal-header" }
const _hoisted_20 = {
  key: 0,
  class: "event-details"
}
const _hoisted_21 = { class: "event-date" }
const _hoisted_22 = { key: 0 }
const _hoisted_23 = {
  key: 0,
  class: "event-description"
}
const _hoisted_24 = { class: "modal-actions" }

import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import axios from "axios";
import { API_URL } from "../config";
import { getErrorMessage } from "../types";
import type { CalendarDayCell, CalendarEventRecord } from "../types";


export default /*#__PURE__*/_defineComponent({
  __name: 'CalendarEventsView',
  props: {
    calendarId: {}
  },
  setup(__props: any) {

const props = __props;

const router = useRouter();

// State
const currentDate = ref(new Date());
const events = ref<CalendarEventRecord[]>([]);
const loading = ref(true);
const error = ref("");
const showEventModal = ref(false);
const selectedDate = ref<Date | null>(null);
const selectedEvent = ref<CalendarEventRecord | null>(null);

// Computed
const currentMonthYear = computed(() => {
  return currentDate.value.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
});

const weekDays = computed(() => {
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
});

const calendarDays = computed(() => {
  const year = currentDate.value.getFullYear();
  const month = currentDate.value.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  // Add days from previous month
  const firstDayWeekday = firstDay.getDay();
  for (let i = firstDayWeekday - 1; i >= 0; i--) {
    const date = new Date(year, month, -i);
    days.push({
      date,
      dayNumber: date.getDate(),
      isCurrentMonth: false,
      events: getEventsForDate(date),
    });
  }

  // Add days from current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    const date = new Date(year, month, i);
    days.push({
      date,
      dayNumber: i,
      isCurrentMonth: true,
      events: getEventsForDate(date),
    });
  }

  // Add days from next month
  const remainingDays = 42 - days.length; // 6 weeks * 7 days = 42
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(year, month + 1, i);
    days.push({
      date,
      dayNumber: date.getDate(),
      isCurrentMonth: false,
      events: getEventsForDate(date),
    });
  }

  return days;
});

// Methods
const getEventsForDate = (date: Date) => {
  if (!events.value || !Array.isArray(events.value)) {
    console.warn("events.value is not an array, returning empty array");
    return [];
  }

  return events.value.filter((event) => {
    const eventStartDate = new Date(event.startDate);
    const eventEndDate = event.endDate
      ? new Date(event.endDate)
      : eventStartDate;
    return date >= eventStartDate && date <= eventEndDate;
  });
};

const isToday = (date: Date) => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

const previousMonth = () => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() - 1
  );
};

const nextMonth = () => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() + 1
  );
};

const handleDayClick = (day: CalendarDayCell) => {
  selectedDate.value = day.date;
  selectedEvent.value = day.events[0] || null;
  showEventModal.value = true;
};

const closeEventModal = () => {
  showEventModal.value = false;
  selectedDate.value = null;
  selectedEvent.value = null;
};

const navigateToAddEvent = () => {
  if (selectedDate.value) {
    router.push({
      path: `/calendar/${props.calendarId}/events/add`,
      query: { date: selectedDate.value.toISOString() },
    });
  }
};

const editEvent = () => {
  if (selectedEvent.value) {
    router.push(
      `/calendar/${props.calendarId}/events/${selectedEvent.value.id}/edit`
    );
  }
};

const confirmDelete = () => {
  if (selectedEvent.value) {
    deleteEvent(selectedEvent.value.id);
  }
};

const deleteEvent = async (eventId: number) => {
  try {
    await axios.delete(
      `${API_URL}/calendar/${props.calendarId}/events/${eventId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    // Refresh events
    fetchEvents();
    closeEventModal();
  } catch (err: unknown) {
    error.value = getErrorMessage(err, "Failed to delete event");
    console.error("Error deleting event:", err);
  }
};

const fetchEvents = async () => {
  loading.value = true;
  error.value = ""; // Reset error state
  try {
    console.log("Fetching events for calendar:", props.calendarId);
    const response = await axios.get(
      `${API_URL}/calendar/${props.calendarId}/events`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    console.log("Events API Response:", response.data);

    // Ensure we're setting an array
    events.value = Array.isArray(response.data) ? response.data : [];
    console.log("Updated events.value:", events.value);
  } catch (err: unknown) {
    console.error("Error fetching events:", err);
    error.value = getErrorMessage(err, "Failed to fetch events");
    events.value = []; // Reset to empty array on error
  } finally {
    loading.value = false;
  }
};

// Format functions
const formatDate = (dateString: string): string => {
  if (!dateString) return "N/A";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatEventType = (type: string): string => {
  if (!type) return "";

  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const addEvent = () => {
  if (selectedDate.value) {
    router.push({
      path: `/calendar/${props.calendarId}/events/add`,
      query: { date: selectedDate.value.toISOString() },
    });
  }
};

// Lifecycle hooks
onMounted(() => {
  fetchEvents();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("button", {
          class: "btn-add-event",
          onClick: navigateToAddEvent
        }, [
          _createVNode(_unref(FontAwesomeIcon), { icon: "plus" }),
          _createTextVNode(" Add Event ")
        ])
      ])
    ]),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, _hoisted_7))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(error.value), 1))
        : (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("button", {
                class: "btn-nav",
                onClick: previousMonth
              }, [
                _createVNode(_unref(FontAwesomeIcon), { icon: "chevron-left" })
              ]),
              _createElementVNode("h3", null, _toDisplayString(currentMonthYear.value), 1),
              _createElementVNode("button", {
                class: "btn-nav",
                onClick: nextMonth
              }, [
                _createVNode(_unref(FontAwesomeIcon), { icon: "chevron-right" })
              ])
            ]),
            _createElementVNode("div", _hoisted_11, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(weekDays.value, (day) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: day,
                  class: "weekday"
                }, _toDisplayString(day), 1))
              }), 128))
            ]),
            _createElementVNode("div", _hoisted_12, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calendarDays.value, (day) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: day.date.toISOString(),
                  class: _normalizeClass(["calendar-day", {
            'other-month': !day.isCurrentMonth,
            'has-events': day.events.length > 0,
            today: isToday(day.date),
          }]),
                  onClick: ($event: any) => (handleDayClick(day))
                }, [
                  _createElementVNode("div", _hoisted_14, _toDisplayString(day.dayNumber), 1),
                  (day.events.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(day.events.slice(0, 2), (event) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: event.id,
                            class: _normalizeClass(["event-dot", event.type])
                          }, null, 2))
                        }), 128)),
                        (day.events.length > 2)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_16, " +" + _toDisplayString(day.events.length - 2), 1))
                          : _createCommentVNode("", true)
                      ]))
                    : _createCommentVNode("", true)
                ], 10, _hoisted_13))
              }), 128))
            ])
          ])),
    (showEventModal.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("div", _hoisted_19, [
              _createElementVNode("h3", null, _toDisplayString(selectedDate.value ? "Add Event" : "Edit Event"), 1),
              _createElementVNode("button", {
                class: "btn-close",
                onClick: closeEventModal
              }, [
                _createVNode(_unref(FontAwesomeIcon), { icon: "times" })
              ])
            ]),
            (selectedEvent.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                  _createElementVNode("h4", null, _toDisplayString(selectedEvent.value.title), 1),
                  _createElementVNode("p", {
                    class: _normalizeClass(["event-type", selectedEvent.value.type])
                  }, _toDisplayString(formatEventType(selectedEvent.value.type)), 3),
                  _createElementVNode("p", _hoisted_21, [
                    _createTextVNode(_toDisplayString(formatDate(selectedEvent.value.startDate)) + " ", 1),
                    (
                selectedEvent.value.endDate &&
                selectedEvent.value.endDate !== selectedEvent.value.startDate
              )
                      ? (_openBlock(), _createElementBlock("span", _hoisted_22, " - " + _toDisplayString(formatDate(selectedEvent.value.endDate)), 1))
                      : _createCommentVNode("", true)
                  ]),
                  (selectedEvent.value.description)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_23, _toDisplayString(selectedEvent.value.description), 1))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_24, [
              (selectedEvent.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    class: "btn-edit",
                    onClick: editEvent
                  }, [
                    _createVNode(_unref(FontAwesomeIcon), { icon: "edit" }),
                    _createTextVNode(" Edit Event ")
                  ]))
                : _createCommentVNode("", true),
              (selectedEvent.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    class: "btn-delete",
                    onClick: confirmDelete
                  }, [
                    _createVNode(_unref(FontAwesomeIcon), { icon: "trash" }),
                    _createTextVNode(" Delete Event ")
                  ]))
                : _createCommentVNode("", true),
              (!selectedEvent.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 2,
                    class: "btn-add",
                    onClick: addEvent
                  }, [
                    _createVNode(_unref(FontAwesomeIcon), { icon: "plus" }),
                    _createTextVNode(" Add Event ")
                  ]))
                : _createCommentVNode("", true)
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})